import { useEffect, useState } from "react";
import ReactDOMServer from 'react-dom/server'
import Modal from "../../../components/base/Modal";
import AvatarUploade from "../../../components/form/AvatarUploade";
import DropDown from "../../../components/form/DropDown";
import TextBox from "../../../components/form/TextBox";
import { API_ROUTES } from "../../../core/Constants";
import InventoryItem from "../models/InventoryItem";
import { CategoryAttributeItem, CategoryAttributeTypes } from "../models/CategoryModel";
import WarehouseContainerModel, { WarehouseContainerStatus } from "../models/WarehouseContainerModel";
import FlexRow from "../../../components/base/FlexRow";
import Icon from "../../../components/base/Icon";
import { ColorModel } from "../models/ColorModel";
import DatePicker from "../../../components/form/DatePicker";
import Tagify from "../../../components/tagify/Tagify";
import { useHub } from "../../../core/Global";
import { DrawerForm } from "../../../components/drawers/DrawerForm";
import ToggleSwitch from "../../../components/form/ToggleSwitch";



interface IProps {
    model?: InventoryItem
    onClose: () => void
    onSave: () => void
}

export default function InventoryItemForm(props: IProps) {

    const hub = useHub()


    const [selectedVendorId, selectedVendorIdSet] = useState<string>()
    const [selectedBrandId, selectedBrandIdSet] = useState<string>()
    const [selectedCategoryId, selectedCategoryIdSet] = useState<string>() 
    const [categoryAtts, categoryAttsSet] = useState<CategoryAttributeItem[]>([]) 
    const [categoryParentAtts, categoryParentAttsSet] = useState<CategoryAttributeItem[]>([])

    const [sizeUnit, sizeUnitSet] = useState<"mm" | "cm" | "m" | "in">("cm")
    const toggleSizeUnit = () => {
        if (sizeUnit == "mm") sizeUnitSet("cm")
        if (sizeUnit == "cm") sizeUnitSet("m")
        if (sizeUnit == "m") sizeUnitSet("in")
        if (sizeUnit == "in") sizeUnitSet("mm")
    }

    const [weightUnit, weightUnitSet] = useState<"lbs" | "g" | "kg">("lbs")
    const toggleWeightUnit = () => {
        if (weightUnit == "lbs") weightUnitSet("g")
        if (weightUnit == "g") weightUnitSet("kg")
        if (weightUnit == "kg") weightUnitSet("lbs")
    }

    useEffect(() => {
        if (props.model) {
            selectedVendorIdSet(props.model.vendorId)
            selectedBrandIdSet(props.model.brandId)
        }
    }, [props.model])

    useEffect(() => {
        selectedCategoryIdSet(props.model?.categoryId)
    }, [props.model?.id, props.model?.categoryId])

    useEffect(() => {
        if (!selectedCategoryId) {
            categoryAttsSet([])
            categoryParentAttsSet([])
            return
        }
        hub.Get(`${API_ROUTES.InventoryCategories}attributes/${selectedCategoryId}`, res => {
            categoryAttsSet(res.data?.atts ?? [])
            categoryParentAttsSet(res.data?.parentAtts ?? [])
        })
        
    }, [selectedCategoryId])

    return (
        <DrawerForm id="inventory-item-form"
            title={props.model ? "Edit Item" : "Add Inventory Item"}
            width={"80%"}
            buttonText={props.model ? "Update Item" : "Add Item"}
            onClose={props.onClose}
            focusOn="txt-inv-item-name"
            onSubmit={(body, callback) => {

                var valid = true;
                var focus = true;

                if (!body.name || body.name.length < 3) {
                    window.formError("name", "Please write a valid name!", "inventory-item-form", focus);
                    focus = false;
                    valid = false;
                }

                if (window.isNullOrEmpty(body.sku) || body.sku.length < 3) {
                    window.formError("sku", "SKU is not valid!", "inventory-item-form", focus);
                    focus = false;
                    valid = false;
                }

                if (window.isNullOrEmpty(body.partNumber) || body.partNumber.length < 2) {
                    window.formError("partNumber", "Part Number is not valid!", "inventory-item-form", focus);
                    focus = false;
                    valid = false;
                }

                if (window.isNullOrEmpty(body.sellingPrice) || isNaN(Number.parseFloat(body.sellingPrice))) {
                    window.formError("sellingPrice", "Price is not valid!", "inventory-item-form", focus);
                    focus = false;
                    valid = false;
                }

                if (window.isNullOrEmpty(body.categoryId)) {
                    window.formError("categoryId", "Please select a category!", "inventory-item-form", focus);
                    focus = false;
                    valid = false;
                }


                if (window.isNullOrEmpty(body.vendorId)) {
                    window.formError("vendorId", "Please select a vendor!", "inventory-item-form", focus);
                    focus = false;
                    valid = false;
                }

                if (window.isNullOrEmpty(body.brandId)) {
                    window.formError("brandId", "Please select a brand!", "inventory-item-form", focus);
                    focus = false;
                    valid = false;
                }

                if (window.isNullOrEmpty(body.containerId)) {
                    window.formError("containerId", "Please select a container!", "inventory-item-form", focus);
                    focus = false;
                    valid = false;
                }

                if (!valid) {
                    callback!()
                    return;
                }

                const attributesList = Object.fromEntries(
                    Object.entries(body)
                        .filter(([key]) => key.startsWith("catAttr_"))
                        .map(([key, value]) => {
                            const attributeId = key.replace("catAttr_", "")

                            if (value === "" || value == null || Number(value) === 0) {
                                return [attributeId, null]
                            }

                            return [attributeId, value ?? null]
                        })
                )

                const cleanBody = Object.fromEntries(
                    Object.entries(body)
                        .filter(([key]) => !key.startsWith("catAttr_"))
                )


                hub.Post(API_ROUTES.InventoryItems, {
                    ...cleanBody,
                    attributesList,
                    id: props.model?.id,
                    sizeUnit: sizeUnit,
                    weightUnit: weightUnit
                }, res => {
                    if (res.success) {
                        props.onSave();
                    }
                    callback!(res);
                })
            }}
        >

            <div className="row">

                <div className="col-12 col-md-8">

                    <TextBox id="txt-inv-item-name" name="name"
                        labelUp block label="Part Name"
                        placeholder="Enter descriptive part name"
                        marginBottom={5}
                        value={props.model?.name} />

                    <div className="row">
                        <div className="col-6">
                            <TextBox id="txt-inv-item-sku" name="sku"
                                labelUp block label="SKU"
                                placeholder="Stock keeping unit identifier"
                                marginBottom={5} icon="barcode"
                                value={props.model?.sku} />
                        </div>
                        <div className="col-6">
                            <TextBox id="txt-inv-item-partnumber" name="partNumber"
                                labelUp block label="Part Number"
                                placeholder="Manufacturer part number"
                                marginBottom={5} icon="hashtag"
                                value={props.model?.partNumber} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <TextBox id="txt-inv-item-sellingPrice" name="sellingPrice"
                                labelUp block label="Selling Price"
                                placeholder="0.00"
                                marginBottom={5} money icon="dollar-sign"
                                value={props.model?.sellingPrice}
                            />
                        </div>
                        <div className="col-6">
                            <TextBox id="txt-inv-item-costPrice" name="costPrice"
                                labelUp block label="Cost Price"
                                placeholder="0.00"
                                marginBottom={5} money icon="dollar-sign"
                                value={props.model?.costPrice} />
                        </div>
                    </div>

                </div>

                <div className="col-12 col-md-4">

                    <div className="text-center mb-5">
                        <AvatarUploade preventCrop type="products"
                            name="imagePath" width={150} extHelp
                            imageUrl={props.model?.thumbUrl}
                            defaultValue={props.model?.imagePath} />
                    </div>

                </div>

                <div className="row">
                    <div className="col-12 col-md-8">
                        <DropDown id="inv-item-categories" name="categoryId"
                            label="Category" labelUp block marginBottom={5}
                            parentModal="inventory-item-form"
                            apiSource={API_ROUTES.InventoryCategories + "all"}
                            dataText="fullPathName" dataValue="id"
                            onSelect={id => selectedCategoryIdSet(id)} 
                            onClear={() => selectedCategoryIdSet(undefined)}
                            forceSelectValue={props.model?.categoryId} />
                    </div>
                    <div className="col-12 col-md-4">
                        <DropDown id="inv-item-units" name="unitId"
                            label="Measurement Unit" labelUp block marginBottom={5}
                            parentModal="inventory-item-form"
                            apiSource={API_ROUTES.InventoryUnits + "all"}
                            forceSelectValue={props.model?.unitId} />
                    </div>
                </div>


                <div className="row">
                    <div className="col-12 col-md-4">
                        <DropDown id="inv-item-vendors" name="vendorId"
                            label="Vendor" labelUp block marginBottom={5}
                            parentModal="inventory-item-form"
                            apiSource={API_ROUTES.InventoryVendors + "all"}
                            onSelect={e => selectedVendorIdSet(e)}
                            forceSelectValue={props.model?.vendorId} />
                    </div>
                    <div className="col-12 col-md-4">
                        <DropDown id="inv-item-brands" name="brandId"
                            label="Brand" labelUp block marginBottom={5}
                            parentModal="inventory-item-form"
                            apiSource={`${API_ROUTES.InventoryBrands}all/?vendorId=${selectedVendorId}`}
                            onSelect={e => selectedBrandIdSet(e)}
                            forceSelectValue={props.model?.brandId} />
                    </div>
                    <div className="col-12 col-md-4">
                        <DropDown id="inv-item-models" name="modelId"
                            label="Model" labelUp block marginBottom={5}
                            parentModal="inventory-item-form"
                            apiSource={`${API_ROUTES.InventoryBrandsModels}all/?brandId=${selectedBrandId}`}
                            forceSelectValue={props.model?.modelId} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 col-md-8">
                        <DropDown id="inv-item-containers" name="containerId"
                            label="Primary Container" labelUp block marginBottom={5}
                            parentModal="inventory-item-form"
                            apiSource={API_ROUTES.InventoryWarehouseContainers + "drp"}
                            forceSelectValue={props.model?.primaryContainerId}
                            templateResult={(data: any) => {
                                const ds = data.element?.dataset || {}
                                const item: WarehouseContainerModel = ds.item ? JSON.parse(decodeURIComponent(ds.item)) : null
                                if (!item) return undefined;
                                return ReactDOMServer.renderToString(
                                    <div className="s2-row">
                                        <div>
                                            {item.name}
                                        </div>
                                        <FlexRow>
                                            <FlexRow className="me-5">
                                                <Icon icon="hashtag" type="solid" className="me-1" size={9} />
                                                <span className="fs-8">
                                                    {item.containerID}
                                                </span>
                                            </FlexRow>
                                            {
                                                item.operatinalStatus == WarehouseContainerStatus.Active &&
                                                <FlexRow>
                                                    <Icon icon="circle" type="solid" className="text-success me-1" size={9} />
                                                    <span className="fs-8">Active</span>
                                                </FlexRow>
                                            }

                                            {
                                                item.operatinalStatus == WarehouseContainerStatus.Inactive &&
                                                <FlexRow>
                                                    <Icon icon="circle" type="solid" className="text-danger me-1" size={9} />
                                                    <span className="fs-8">Inactive</span>
                                                </FlexRow>
                                            }

                                            {
                                                item.operatinalStatus == WarehouseContainerStatus.UnderMaintenance &&
                                                <FlexRow>
                                                    <Icon icon="circle" type="solid" className="text-warning me-1" size={9} />
                                                    <span className="fs-8">Under Maintenance</span>
                                                </FlexRow>
                                            }
                                        </FlexRow>
                                    </div>
                                )
                            }}
                            templateSelection={(data: any) => {
                                const ds = data.element?.dataset || {}
                                const item: WarehouseContainerModel = ds.item ? JSON.parse(decodeURIComponent(ds.item)) : null
                                if (!item) return ReactDOMServer.renderToString(<div>Choose a Container</div>);
                                return ReactDOMServer.renderToString(
                                    <div className="s2-row">
                                        <FlexRow>
                                            <div className="me-4">
                                                {item.name}
                                            </div>
                                            {
                                                item.operatinalStatus == WarehouseContainerStatus.Active &&
                                                <FlexRow>
                                                    <Icon icon="circle" type="solid" className="text-success me-1" size={9} />
                                                    <span className="fs-8">Active</span>
                                                </FlexRow>
                                            }

                                            {
                                                item.operatinalStatus == WarehouseContainerStatus.Inactive &&
                                                <FlexRow>
                                                    <Icon icon="circle" type="solid" className="text-danger me-1" size={9} />
                                                    <span className="fs-8">Inactive</span>
                                                </FlexRow>
                                            }

                                            {
                                                item.operatinalStatus == WarehouseContainerStatus.UnderMaintenance &&
                                                <FlexRow>
                                                    <Icon icon="circle" type="solid" className="text-warning me-1" size={9} />
                                                    <span className="fs-8">Under Maintenance</span>
                                                </FlexRow>
                                            }
                                        </FlexRow>
                                    </div>
                                )
                            }}
                        />
                    </div>
                    <div className="col-12 col-md-4">
                        <DropDown id="inv-item-colors" name="colorId"
                            label="Color" labelUp block marginBottom={5}
                            parentModal="inventory-item-form"
                            apiSource={API_ROUTES.InventoryColors + "drp"}
                            forceSelectValue={props.model?.colorId}
                            templateResult={(data: any) => {
                                const ds = data.element?.dataset || {}
                                const item: ColorModel = ds.item ? JSON.parse(decodeURIComponent(ds.item)) : null
                                if (!item) return undefined;
                                return ReactDOMServer.renderToString(
                                    <div className="s2-row">
                                        <FlexRow>
                                            <div className="me-2" style={{
                                                width: 20,
                                                height: 20,
                                                backgroundColor: item.hexCode,
                                                border: "1px solid #000",
                                                borderRadius: 10
                                            }}>
                                            </div>
                                            <span>{item.name}</span>
                                        </FlexRow>
                                    </div>
                                )
                            }}
                            templateSelection={(data: any) => {
                                const ds = data.element?.dataset || {}
                                const item: ColorModel = ds.item ? JSON.parse(decodeURIComponent(ds.item)) : null
                                if (!item) return ReactDOMServer.renderToString(<div>Choose a Color</div>);
                                return ReactDOMServer.renderToString(
                                    <div className="s2-row">
                                        <FlexRow>
                                            <div className="me-2" style={{
                                                width: 18,
                                                height: 18,
                                                backgroundColor: item.hexCode,
                                                border: "1px solid #000",
                                                borderRadius: 9
                                            }}>
                                            </div>
                                            <span>{item.name}</span>
                                        </FlexRow>
                                    </div>
                                )
                            }}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 col-md-3">
                        <TextBox id="txt-inv-item-length" name="length"
                            labelUp block label="Length" money
                            marginBottom={5} placeholder="0"
                            iconText={sizeUnit}
                            iconClick={toggleSizeUnit}
                            value={props.model?.itemSizeLength} />
                    </div>
                    <div className="col-12 col-md-3">
                        <TextBox id="txt-inv-item-width" name="width"
                            labelUp block label="Width" money
                            marginBottom={5} placeholder="0"
                            iconText={sizeUnit}
                            iconClick={toggleSizeUnit}
                            value={props.model?.itemSizeWidth} />
                    </div>
                    <div className="col-12 col-md-3">
                        <TextBox id="txt-inv-item-height" name="height"
                            labelUp block label="Height" money
                            marginBottom={5} placeholder="0"
                            iconText={sizeUnit}
                            iconClick={toggleSizeUnit}
                            value={props.model?.itemSizeHeight} />
                    </div>
                    <div className="col-12 col-md-3">
                        <TextBox id="txt-inv-item-weight" name="weight"
                            labelUp block label="Weight" money
                            marginBottom={5} placeholder="0"
                            iconText={weightUnit}
                            iconClick={toggleWeightUnit}
                            value={props.model?.itemWeight} />
                    </div>
                </div>


                <div className="row">
                    <div className="col-12 col-md-4">
                        <TextBox id="txt-inv-item-initianqty" name="initialQty"
                            labelUp block label="Initial Quantity" numeric
                            marginBottom={5} placeholder="0"
                            value={props.model?.initialQuantity} />
                    </div>
                    <div className="col-12 col-md-4">
                        <TextBox id="txt-inv-item-lowstockqty" name="lowStockWarningQty"
                            labelUp block label="Low Stock Warning" numeric
                            marginBottom={5} placeholder="0"
                            value={props.model?.lowStockWarning} />
                    </div>
                    <div className="col-12 col-md-4">
                        <TextBox id="txt-inv-item-parlevel" name="parLevel"
                            labelUp block label="PAR Level" numeric
                            marginBottom={5} placeholder="Periodic Auto Replenishment Level"
                            value={props.model?.parLevel} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 col-md-6">
                        <DropDown id="inv-item-taxes" name="taxId"
                            label="Tax" labelUp block marginBottom={5}
                            parentModal="inventory-item-form"
                            apiSource={API_ROUTES.Taxes + "all"}
                            dataText="nameWithAmount" dataValue="id"
                            forceSelectValue={props.model?.taxId} />
                    </div>
                    <div className="col-12 col-md-6">
                        <DatePicker id="inv-item-prod-date" name="productionDate"
                            labelUp block label="Production Date"
                            marginBottom={5} drops="up"
                            icon="calendar" date={props.model?.productionDate} />
                    </div>
                </div>

                <Tagify id="inv-item-tags" name="tags"
                    labelUp block label="Tags" freeTags
                    apiUrl={API_ROUTES.TagifyInventoryTags}
                    selectedIds={props.model?.tagIds}
                    marginBottom={5} />



                {/* Start custom fields section */}

                <div className="row">
                    
                    {[...categoryParentAtts, ...categoryAtts].map((att, k) => {
                        const attributeKey = att.id ?? k.toString()
                        const attributeValue = props.model?.attributesList?.[attributeKey] ?? null

                        if (att.type === CategoryAttributeTypes.Boolean) {
                            return (
                                <div className="col-12 col-md-3 mb-5" key={attributeKey}>
                                    <DropDown id={`inv-cat-att-${attributeKey}`}
                                        name={`catAttr_${attributeKey}`}
                                        labelUp block
                                        label={att.name?.trim() ? att.name : `Custom field ${k + 1}`}
                                        marginBottom={5}
                                        placeholder="Choose one"
                                        source={[
                                            { id: "false", name: "No" },
                                            { id: "true", name: "Yes" }
                                        ]}
                                        parentModal="inventory-item-form"
                                        forceSelectValue={attributeValue?.toString().toLowerCase() === "true" ? "true" : "false"} />
                                </div>
                            )
                        }

                        if (att.type === CategoryAttributeTypes.Numeric) {
                            return (
                                <div className="col-12 col-md-3 mb-5" key={attributeKey}>
                                    <TextBox id={`inv-cat-att-${attributeKey}`}
                                        name={`catAttr_${attributeKey}`}
                                        labelUp block
                                        label={att.name?.trim() ? att.name : `Custom field ${k + 1}`}
                                        numeric
                                        marginBottom={5} placeholder="0"
                                        value={attributeValue == null || Number(attributeValue) === 0 ? null : attributeValue} />
                                </div>
                            )
                        }

                            return (
                                <div className="col-12 col-md-3 mb-5" key={attributeKey}>
                                    <TextBox id={`inv-cat-att-${attributeKey}`}
                                        name={`catAttr_${attributeKey}`}
                                        labelUp block
                                        label={att.name?.trim() ? att.name : `Custom field ${k + 1}`}
                                        marginBottom={5}
                                        placeholder="Choose one"
                                        value={attributeValue} />
                                </div>
                            )
                    })}

                </div>






            </div>

        </DrawerForm>
    )
}